import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Botao, ETAPAS_ADESAO } from '../../ui';
import { RootState } from '../../store/store';
import {
  FluxoDeAdesao,
  rotaDaEtapaDoFluxo,
  situacaoDasEtapasDaAdesao,
} from '../../utils/adesaoFluxo';

/**
 * A etapa 3 do fluxo **não abre** se alguma anterior estiver incompleta (at-62).
 *
 * ## O defeito que isto corrige
 *
 * 🔴 Victor, 10/09/2026: chegou a `/documento` com as bolinhas 1 e 2 **vermelhas**,
 * clicou em "Cadastrar Associado e Veículo" e recebeu só *"Erro ao finalizar o
 * cadastro."*. O pedido dele: *"essa etapa precisa bloquear a possibilidade de
 * fotos se alguma das anteriores não estiver completa"*.
 *
 * Duas coisas estavam erradas ali:
 *
 *   1. **O cadastro era garantidamente recusado.** Os campos que faltavam são
 *      `@IsNotEmpty` em `CreateAssociadoDto` — o `POST` volta 400 (ou, no caso do
 *      `sexo`, a tela estoura antes de chamar, porque faz `.toUpperCase()` sem
 *      `?.`). O consultor só via a mensagem genérica, sem saber o que faltava.
 *   2. **As fotos se perderiam.** O upload só acontece **depois** do cadastro
 *      (at-38: é ele que dá `associadoId`/`carroId` às imagens). Fotografar
 *      documento antes de um cadastro que vai falhar é trabalho jogado fora — e
 *      no celular é o trabalho mais caro da etapa.
 *
 * ## Por que aqui BLOQUEIA e na cotação só AVISA
 *
 * ⚠️ A diferença não é de estilo. Em `/cotacao-produtos` a lista de pendências é
 * uma **heurística** sobre o que o PDF precisa, e gerar é barato e repetível —
 * uma regra local mais estrita que a do servidor barraria cotação legítima. Aqui
 * a lista é um **subconjunto do que a DTO exige**: o que ela acusa, o servidor
 * recusa. Bloquear não fecha nenhuma porta que estivesse aberta.
 */

/**
 * As etapas anteriores que impedem o cadastro, e se ele está bloqueado.
 *
 * ⚠️ Duas fatias do store, não o estado inteiro — com `(s) => s` o componente
 * re-renderizaria a cada mudança de qualquer slice do app.
 */
export function useEtapasIncompletas(etapa: number): {
  pendentes: number[];
  nomes: string[];
  bloqueado: boolean;
} {
  const associado = useSelector((state: RootState) => state.associado);
  const respostasDoSga = useSelector((state: RootState) => state.respostasDoSga);

  const { pendentes } = situacaoDasEtapasDaAdesao({ associado, respostasDoSga }, etapa);

  return {
    pendentes,
    nomes: pendentes.map((numero) => ETAPAS_ADESAO[numero - 1]).filter(Boolean),
    bloqueado: pendentes.length > 0,
  };
}

interface PropsFaixa {
  etapa: number;
  /**
   * Fluxo em curso — decide para onde o botão de correção leva. `null` (o caso
   * de `/documento/edit`, que serve as duas substituições) esconde o botão: sem
   * saber o fluxo, o destino seria um chute (ver `rotaDaEtapaDoFluxo`).
   */
  fluxo: FluxoDeAdesao | null;
}

const FaixaEtapasIncompletas: React.FC<PropsFaixa> = ({ etapa, fluxo }) => {
  const navigate = useNavigate();
  const { pendentes, nomes } = useEtapasIncompletas(etapa);

  if (pendentes.length === 0) return null;

  const primeira = pendentes[0];
  const rota = rotaDaEtapaDoFluxo(fluxo, primeira);

  return (
    /*
     * Vermelha, como as bolinhas — aqui "pendente" significa que o cadastro não
     * vai sair, não que algo se resolve sozinho no cron (que é o âmbar do `Chip`).
     * `role="alert"` para o leitor de tela anunciar sem o usuário ir procurar.
     */
    <div
      className="tw-mb-6 tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-danger/30 tw-bg-danger-bg tw-px-4 tw-py-3"
      role="alert"
    >
      <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-danger">
        {nomes.length === 1
          ? `A etapa ${nomes[0]} está incompleta.`
          : `As etapas ${nomes.join(' e ')} estão incompletas.`}
      </p>
      <p className="tw-m-0 tw-text-xs tw-text-texto-corpo">
        O cadastro seria recusado e as fotos se perderiam — elas só são enviadas depois que o
        associado e o veículo existem. Complete a etapa e volte para cá.
      </p>
      {rota && (
        <Botao
          variante="secundario"
          tamanho="sm"
          iconeEsquerda="voltar"
          className="sm:tw-self-start"
          onClick={() => navigate(rota)}
        >
          Ir para {nomes[0]}
        </Botao>
      )}
    </div>
  );
};

export default FaixaEtapasIncompletas;
